<template>
  <div class="voice-call">
    <div class="voice-controls">
      <button
        type="button"
        class="voice-btn"
        v-if="!joined"
        @click="joinCall"
        title="Join Voice Call"
      >
        <microphone-icon class="voice-icon" />
      </button>
      <button
        v-if="joined"
        @click="endCall"
        type="button"
        class="voice-btn voice-btn--active"
        title="End Voice Call"
      >
        <mute-microphone-icon class="voice-icon" />
      </button>
    </div>

    <div v-if="joined" class="active-participants">
      <div v-for="user in participants" :key="user.userId" class="participant">
        <div class="participant-indicator">
          <span class="participant-dot"></span>
        </div>
        <span class="participant-name">{{
          getParticipantName(user.userId)
        }}</span>
      </div>
    </div>

    <div v-if="joined" class="voice-status">
      <speaker-icon class="voice-icon" />
      <span class="timer">{{ formattedDuration }}</span>
    </div>

    <div style="display: none" ref="remoteAudioContainer"></div>
  </div>
</template>

<script>
import Peer from 'peerjs'
import io from 'socket.io-client'
import config from '@/config'
import { getPeerHost } from '@/config/peerHost'
import MicrophoneIcon from '@/assets/microphone.svg'
import MuteMicrophoneIcon from '@/assets/mute-microphone.svg'

const SpeakerIcon = {
  functional: true,
  render(h, context) {
    return h(
      'svg',
      {
        ...context.data,
        attrs: {
          xmlns: 'http://www.w3.org/2000/svg',
          viewBox: '0 0 24 24',
          fill: 'currentColor',
        },
      },
      [
        h('path', {
          attrs: {
            d:
              'M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z',
          },
        }),
      ]
    )
  },
}

export default {
  name: 'VoiceCall',
  components: {
    MicrophoneIcon,
    MuteMicrophoneIcon,
    SpeakerIcon,
  },
  props: {
    sessionId: { type: String, required: true },
    currentUserId: { type: String, required: true },
  },
  data() {
    return {
      socket: null,
      peer: null,
      peerId: '',
      myStream: null,
      joined: false,
      participants: [],
      peerConnections: {},
      remoteAudioElements: {},
      callTimer: null,
      callDurationSeconds: 0,
    }
  },
  computed: {
    roomId() {
      return this.sessionId
    },
    formattedDuration() {
      const minutes = Math.floor(this.callDurationSeconds / 60)
      const seconds = this.callDurationSeconds % 60
      return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(
        2,
        '0'
      )}`
    },
  },
  mounted() {
    // اتصال سوکت اختصاصی
    // Task 07 / STEP-05 (FR-09): reads config.socketAddress (the same
    // canonical value src/main.js:50 already connects the app-wide socket
    // to) instead of a hardcoded core.bornadanesh.com literal. peer.
    // (:146) and turn. (:153-165) below are unchanged -- task 03's, pending
    // ADR-03-001.
    this.socket = io(config.socketAddress)

    this.socket.on('connect', () => {
      console.log('✅ Voice Call Socket Connected! ID:', this.socket.id)
      this.initializePeer()
    })

    this.socket.on('connect_error', err => {
      console.error('❌ Voice Call Socket Connection Error:', err)
    })
  },
  beforeDestroy() {
    // فقط در زمان حذف کامل کامپوننت، همه چیز را از بین می‌بریم
    this.cleanup()
  },
  methods: {
    startTimer() {
      this.stopTimer() // جلوگیری از اجرای چند تایمر همزمان
      this.callDurationSeconds = 0
      this.callTimer = setInterval(() => {
        this.callDurationSeconds++
      }, 1000)
    },
    stopTimer() {
      clearInterval(this.callTimer)
      this.callTimer = null
    },
    initializePeer() {
      if (this.peer && !this.peer.destroyed) return
      try {
        this.peer = new Peer(undefined, {
          host: getPeerHost(),
          port: '',
          path: '/peerjs',
          debug: 0,
          config: {
            iceServers: [
              {
                urls: 'stun:turn.bornadanesh.com:3478',
              },
              {
                urls: [
                  'turn:turn.bornadanesh.com:3478?transport=udp',
                  'turn:turn.bornadanesh.com:3478?transport=tcp',
                ],
                username: 'testuser',
                credential: 'testpass',
              },
              {
                urls: 'turns:turn.bornadanesh.com:5349?transport=tcp',
                username: 'testuser',
                credential: 'testpass',
              },
            ],
          },
        })
        this.peer.on('open', id => {
          this.peerId = id
          console.log(
            '✅ PeerJS Initialized or Re-initialized. My Peer ID:',
            id
          )
        })
        this.peer.on('call', this.handleIncomingCall)
        this.peer.on('error', err => console.error('❌ PeerJS Error:', err))
      } catch (error) {
        console.error('❌ Failed to initialize PeerJS:', error)
      }
    },
    async joinCall() {
      if (this.joined) return
      if (!this.peer || this.peer.destroyed) this.initializePeer()
      if (!this.peerId) {
        setTimeout(this.joinCall, 200)
        return
      }
      console.log('▶️ Attempting to join call...')
      try {
        this.myStream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        })
        this.setupSocketListeners()
        this.joined = true
        this.addParticipant(this.peerId)
        this.socket.emit('voice:join', this.roomId, this.peerId)
        this.$emit('joined')
        this.startTimer() // <-- شروع تایمر
        console.log('✅ Emitted "voice:join" to the server.')
      } catch (err) {
        console.error('❌ Could not get microphone:', err)
      }
    },
    setupSocketListeners() {
      this.socket.off('voice:init') // حذف شنونده قبلی برای جلوگیری از تکرار
      this.socket.off('voice:new-peer')
      this.socket.off('user-disconnected')

      this.socket.on('voice:init', this.handleVoiceInit)
      this.socket.on('voice:new-peer', this.handleNewPeer)
      this.socket.on('user-disconnected', this.handleUserDisconnected)
      console.log('🎧 All voice socket listeners attached.')
    },
    handleVoiceInit(data) {
      console.log('🤝 Received voice init data with peers:', data.peers)
      data.peers.forEach(pId => this.connectToNewUser(pId))
    },
    handleNewPeer(data) {
      console.log(`👤 New peer joined: ${data.peerId}`)
      this.connectToNewUser(data.peerId)
    },
    handleUserDisconnected(pId) {
      console.log(`👋 User disconnected: ${pId}`)
      this.removeParticipant(pId)
    },
    connectToNewUser(peerId) {
      if (
        peerId === this.peerId ||
        this.peerConnections[peerId] ||
        !this.myStream ||
        !this.peer
      )
        return
      console.log(`📞 Calling peer: ${peerId}`)
      const call = this.peer.call(peerId, this.myStream)
      this.setupCallListeners(call)
      this.addParticipant(peerId)
      this.peerConnections[peerId] = call
    },
    handleIncomingCall(call) {
      if (!this.myStream) return // اگر استریم نداریم، پاسخ نده
      console.log(`📞 Incoming call from: ${call.peer}`)
      call.answer(this.myStream)
      this.setupCallListeners(call)
      this.addParticipant(call.peer)
      this.peerConnections[call.peer] = call
    },
    setupCallListeners(call) {
      call.on('stream', remoteStream => {
        console.log(`🎵 Received stream from: ${call.peer}`)
        this.addRemoteStream(remoteStream, call.peer)
      })
      call.on('close', () => this.removeParticipant(call.peer))
    },
    async addRemoteStream(stream, peerId) {
      this.cleanupAudioElement(peerId)
      const audio = document.createElement('audio')
      audio.srcObject = stream
      audio.controls = true
      audio.id = `audio-${peerId}`
      this.$refs.remoteAudioContainer.appendChild(audio)
      this.remoteAudioElements[peerId] = audio
      try {
        await audio.play()
        console.log(`✅ Audio stream from ${peerId} is playing.`)
      } catch (error) {
        console.error(`❌ Could not play audio from ${peerId}:`, error)
        if (error.name === 'NotAllowedError') {
          this.$emit('autoplay-blocked')
        }
      }
    },
    endCall() {
      if (!this.joined) return
      console.log('🛑 Ending call but keeping resources alive.')
      this.joined = false
      this.stopTimer()
      if (this.myStream) {
        this.myStream.getTracks().forEach(track => track.stop())
        this.myStream = null
      }

      this.socket.emit('leave-room', this.roomId, this.peerId)

      Object.values(this.peerConnections).forEach(call => call.close())
      this.peerConnections = {}

      Object.keys(this.remoteAudioElements).forEach(this.cleanupAudioElement)
      this.participants = this.participants.filter(
        p => p.userId === this.peerId
      )

      this.$emit('ended')
    },
    // متد پاک‌سازی نهایی برای زمان حذف کامپوننت
    cleanup() {
      this.endCall() // ابتدا تماس را تمام کن
      if (this.socket) {
        this.socket.disconnect()
        this.socket = null
      }
      if (this.peer) {
        this.peer.destroy()
        this.peer = null
      }
      console.log('🔥 All resources cleaned up and destroyed.')
    },
    addParticipant(peerId) {
      if (!this.participants.find(p => p.userId === peerId)) {
        this.participants.push({ userId: peerId })
      }
    },
    removeParticipant(peerId) {
      this.participants = this.participants.filter(p => p.userId !== peerId)
      if (this.peerConnections[peerId]) {
        delete this.peerConnections[peerId]
      }
      this.cleanupAudioElement(peerId)
    },
    cleanupAudioElement(peerId) {
      const audioEl = this.remoteAudioElements[peerId]
      if (audioEl) audioEl.remove()
      delete this.remoteAudioElements[peerId]
    },
    getParticipantName(userId) {
      return userId === this.currentUserId
        ? 'You'
        : `User ${userId.substring(0, 4)}`
    },
  },
}
</script>
<style lang="scss" scoped>
.voice-call {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #f0f4f8;
  padding: 5px 10px; // کمی پدینگ افقی برای فضای بیشتر
  border-radius: 20px;
  width: 100%; // برای اینکه تمام عرض موجود را بگیرد

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 12px;
    padding: 8px 12px;
    justify-content: space-between;
  }
}

.voice-controls {
  flex-shrink: 0;

  @media (max-width: 768px) {
    order: 1;
  }
}

.voice-btn {
  background-color: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  &:hover {
    background-color: #eaeaea;
  }

  &--active {
    background-color: #ffdddd;
    border-color: #ff5555;
    color: #d90429;

    &:hover {
      background-color: #ffcccc;
    }
  }
}

.voice-icon {
  fill: currentColor;
  height: 16px;
  width: 16px;
}

.active-participants {
  display: flex;
  align-items: center;
  gap: 8px;
  overflow-x: auto;
  padding: 2px 0;
  flex-grow: 1;
  min-width: 0;

  @media (max-width: 768px) {
    order: 3;
    width: 100%;
    padding: 4px 0 0 0;
  }
}

.participant {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  background: #ffffff;
  border-radius: 16px;
  font-size: 12px;
  white-space: nowrap;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.participant-indicator {
  display: flex;
  align-items: center;
}

.participant-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #ccc;
}

.voice-status {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
  /* این بخش کلیدی است و این المان را به سمت راست هل می‌دهد */
  padding: 4px 10px;
  background-color: #e9ecef;
  border-radius: 16px;
  color: #495057;
  font-size: 13px;
  font-variant-numeric: tabular-nums; // برای جلوگیری از پرش اعداد تایمر
  flex-shrink: 0;

  @media (max-width: 768px) {
    order: 2;
    margin-left: 0;
  }
}

#remote-audio-container {
  position: absolute;
  top: 10px;
  right: 10px;
  display: flex;
  flex-direction: column;
  gap: 5px;
  background: white;
  padding: 10px;
  border: 1px solid black;
  z-index: 1000;
}
</style>
